import { useEffect, useId, useState, type FormEvent } from 'react';
import { toBerlinDateKey, type FinancingStageBody } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Dialog, RadioCard, Skeleton, TextField, Textarea } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { changeFinancingStage, useStageOptions } from '../api';
import type { FinancingRecord, StageKey } from '../types';
import styles from './dialogs.module.css';

export interface StageChangeDialogProps {
  open: boolean;
  financing: FinancingRecord;
  onClose: () => void;
  onDone: (financing: FinancingRecord) => void;
}

/**
 * „Stufe wechseln" (Finanzierungen 06 button; the dialog is not modelled): the options of
 * `GET /financings/:id/stage-options` — disabled ones with the server's reason —, a mandatory reason for
 * backward moves, „Ausgezahlt am" for „Ausgezahlt" → `POST /financings/:id/stage`.
 */
export function StageChangeDialog({ open, financing, onClose, onDone }: StageChangeDialogProps) {
  const formId = useId();
  const options = useStageOptions(financing.id, open);
  const [target, setTarget] = useState<StageKey | null>(null);
  const [reason, setReason] = useState('');
  const [disbursedOn, setDisbursedOn] = useState(toBerlinDateKey(new Date()));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const selected = options.data?.options.find((option) => option.key === target) ?? null;

  useEffect(() => {
    if (!open) return;
    setTarget(null);
    setReason('');
    setDisbursedOn(toBerlinDateKey(new Date()));
    setErrors({});
    setBanner(null);
  }, [open, financing.id]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    if (!selected) {
      setErrors({ to: 'Bitte wählen Sie eine Stufe.' });
      return;
    }
    const next: Record<string, string> = {};
    if (selected.reasonRequired && reason.trim() === '') next['reason'] = 'Bitte geben Sie einen Grund an.';
    if (selected.requiresDisbursedOn && !disbursedOn)
      next['disbursedOn'] = 'Bitte geben Sie das Auszahlungsdatum an.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    const body: FinancingStageBody = { to: selected.key };
    if (selected.reasonRequired || reason.trim()) body.reason = reason.trim();
    if (selected.requiresDisbursedOn) body.disbursedOn = disbursedOn;
    setPending(true);
    setBanner(null);
    try {
      onDone(await changeFinancingStage(financing.id, body));
    } catch (cause) {
      if (cause instanceof ApiError && Object.keys(cause.fields).length > 0) setErrors(cause.fields);
      else setBanner(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  const anyAllowed = options.data?.options.some((option) => option.allowed) ?? false;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      eyebrow={financing.numberLabel}
      title="Stufe wechseln"
      subtitle={financing.title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={pending} disabled={!anyAllowed}>
            Stufe setzen
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.stack} onSubmit={submit} noValidate>
        {financing.stageChangeHint ? <Banner tone="info">{financing.stageChangeHint}</Banner> : null}
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        {options.isError ? <Banner tone="danger">{errorMessage(options.error)}</Banner> : null}
        {options.isPending ? <Skeleton variant="block" height={160} /> : null}
        {options.data ? (
          <fieldset className={styles.group}>
            <legend className={styles.legend}>Neue Stufe</legend>
            <div className={styles.list}>
              {options.data.options.map((option) => (
                <RadioCard
                  key={option.key}
                  name={`${formId}-stage`}
                  title={option.direction === 'backward' ? `${option.label} · zurück` : option.label}
                  description={option.allowed ? undefined : (option.reason ?? undefined)}
                  value={option.key}
                  checked={target === option.key}
                  disabled={!option.allowed}
                  className={clsx(!option.allowed && styles.disabledOption)}
                  onChange={() => {
                    setTarget(option.key);
                    setErrors({});
                  }}
                />
              ))}
            </div>
            {errors['to'] ? (
              <span className={styles.error} role="alert">
                {errors['to']}
              </span>
            ) : null}
          </fieldset>
        ) : null}
        {selected?.requiresDisbursedOn ? (
          <TextField
            label="Ausgezahlt am"
            type="date"
            required
            value={disbursedOn}
            max={toBerlinDateKey(new Date())}
            error={errors['disbursedOn']}
            onChange={(event) => setDisbursedOn(event.target.value)}
          />
        ) : null}
        {selected && (selected.reasonRequired || selected.direction === 'backward') ? (
          <Textarea
            label="Grund"
            required={selected.reasonRequired}
            rows={3}
            value={reason}
            error={errors['reason']}
            onChange={(event) => setReason(event.target.value)}
          />
        ) : null}
      </form>
    </Dialog>
  );
}
